import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import type { ComponentProps } from 'react'
import React, { useCallback, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import NavBarButton from '../NavBarButton/NavBarButton'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import { useUserSettings } from 'tripPlanner/hooks/api/userSettings'
import { tripLoginModalSignUpView } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Badge from 'LuxKit/Badge'
import { useInView } from 'react-intersection-observer'
import { clearFirstTripItemHasJustBeenAdded } from 'tripPlanner/reducers/actions'
import FirstTripItemOnboardingTooltip from 'tripPlanner/components/Drawer/FirstTripItemOnboardingTooltip'
import { mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Heading from 'LuxKit/Typography/Heading'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import TextButton from 'LuxKit/Button/TextButton'
const TripNavButton = styled(NavBarButton)`
 
    ${mediaQueryUp.tablet} {
      display: flex;
    }
  
`

type InheritedNavBarButtonProps = Pick<ComponentProps<typeof NavBarButton>, 'contentBreakpointMax'>

interface Props extends InheritedNavBarButtonProps {
  className?: string
}

function NavBarTripButton(props: Props) {
  const { className, ...rest } = props
  const intl = useIntl()
  const navRef = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()
  const { data: trips = [] } = useTrips()
  const { data: userSettings } = useUserSettings()
  const userHasBeenAddedToNewTrips = !!userSettings?.recentlyAddedToTripIDs?.length
  const tripItemHasJustBeenAdded = useAppSelector(state => state.tripPlanner.tripItemHasJustBeenAdded)
  const firstItemJustBeenAdded = useAppSelector(state => state.tripPlanner.firstTripItemHasJustBeenAdded)

  const onNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(async() => {
    fireInteractionEvent(tripLoginModalSignUpView('saved_drawer'))
  }, [])

  const [menuItemRef, menuItemInView] = useInView({ triggerOnce: true, skip: !firstItemJustBeenAdded })
  const showTripNotificationDot = userHasBeenAddedToNewTrips || tripItemHasJustBeenAdded
  const showTooltip = menuItemInView && firstItemJustBeenAdded

  const onDismissFirstTripTooltip = useCallback(() => {
    dispatch(clearFirstTripItemHasJustBeenAdded())
  }, [dispatch])

  const isTripChatPageEnabled = useOptimizelyExperiment(OptimizelyFeatureFlags.trpTripChatPage)
  const deviceType = useAppSelector(state => state.system.device.deviceType)
  const isDesktop = deviceType !== 'mobile' && deviceType !== 'tablet'
  const showAITripPlanner = isTripChatPageEnabled && isDesktop
  const [dropdownOpen, toggleDropdown, , closeDropdown] = useToggle(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    menuItemRef(navRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navRef.current])

  const handleNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (showAITripPlanner) {
      e.preventDefault()
      toggleDropdown()
    } else {
      onNavClick(e)
    }
  }, [showAITripPlanner, toggleDropdown, onNavClick])

  const tripPlansText = intl.formatMessage({
    id: 'cKWso/o+4f',
    defaultMessage: 'Trip Plans',
    description: 'Trip Plans navigation button text',
  })

  const tripNavButton = (
    <TripNavButton
      {...rest}
      icon={<LineHeartIcon />}
      contentBreakpointMin="desktop"
      to={showAITripPlanner ? undefined : '/trip-planner'}
      onClick={handleNavClick}
      ref={showAITripPlanner ? dropdownTriggerRef : navRef}
      badge={showTripNotificationDot ? <Badge kind="dot" mode="critical" /> : undefined}
    >
      {tripPlansText}
    </TripNavButton>
  )

  const aiTripPlannerInviteText = intl.formatMessage({
    id: 'ZL48D6s1UX',
    defaultMessage: "You've been invited to try our",
    description: 'AI Trip Planner dropdown invite text',
  })

  const aiTripPlannerTitle = intl.formatMessage({
    id: 'mN6JIH3L7I',
    defaultMessage: 'AI Trip Planner',
    description: 'AI Trip Planner title',
  })

  const betaLabel = intl.formatMessage({
    id: 'boatFyrASB',
    defaultMessage: 'Beta',
    description: 'Beta status label for AI Trip Planner',
  })

  const giveItAGoText = intl.formatMessage({
    id: 'W+nUbiBCPQ',
    defaultMessage: 'Give it a go',
    description: 'Button text to try AI Trip Planner',
  })

  const aiTripPlannerDropdown = (
    <>
      {tripNavButton}
      <DropdownList
        size="M"
        open={dropdownOpen}
        onClose={closeDropdown}
        triggerRef={dropdownTriggerRef}
        anchorRef={dropdownTriggerRef}
        placement="bottom"
        portaled
        hasDismissButton
        headerExtension={
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary">
              {aiTripPlannerInviteText}
            </BodyText>
            <Group direction="horizontal" gap={12}>
              <Heading variant="heading3">
                {aiTripPlannerTitle}
              </Heading>
              <StatusLabel variant="confirmation"> {betaLabel} </StatusLabel>
            </Group>
          </Group>
        }
      >
        <Sheet padding="0 20 20">
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              <FormattedMessage
                id="1WPv+HFKJd"
                defaultMessage="Chat with your personal travel designer who will help{br} craft the perfect itinerary for you."
                description="Description of AI TripPlanner chat feature in navigation bar dropdown"
                values={{ br: <br /> }}
              />
            </BodyText>
            <TextButton kind="primary" size="large" to="/trip-planner/chat-trip-planner" onClick={closeDropdown}>
              {giveItAGoText}
            </TextButton>
          </Group>
        </Sheet>
      </DropdownList>
    </>
  )

  return <TripGuard>
    {showAITripPlanner ? aiTripPlannerDropdown : tripNavButton}
    {showTooltip && trips[0] && (
      <FirstTripItemOnboardingTooltip
        isOpen
        anchorRef={navRef}
        placement="bottom"
        trip={trips[0]}
        onDismiss={onDismissFirstTripTooltip}
      />)}
  </TripGuard>
}

export default React.memo(NavBarTripButton)
